import React from 'react';
import { type MenuProps } from 'antd';
import {
  AlignedInfo,
  Box,
  Button,
  LocalDate,
  Dropdown,
  Flex,
  Gap,
  Icon,
  LoadingState,
  Modal,
  Text,
} from '@common/components';
import { useMutation } from '@common/hooks';
import { useFeatureFlags } from '@common/contexts/feature-flags';
import { createMessage } from 'works/legacy/lib/message-helper';
import { DELETE_WORKER_PROFILE_CHANGE_MUTATION } from '@load-balancing/graphql/mutations/delete-worker-profile-change';
import {
  ftePercentageLabel,
  schedulingStatusLabel,
  shiftPatternLabel,
  shiftPreferenceLabel,
  skillsLabel,
  type TeamReviewChange,
} from '@load-balancing/services';

export function ScheduleWorkerUpcomingChanges({
  changes,
  workerId,
  scheduleGroupId,
  schedulingPeriodId,
  readOnly = false,
  onEdit,
  canManageProfile,
  skillsEnabled = false,
  chargeRoleTerm,
}: {
  changes: TeamReviewChange[];
  workerId: string;
  scheduleGroupId: string;
  schedulingPeriodId?: string | null;
  readOnly?: boolean;
  onEdit: (changeId: string) => void;
  canManageProfile: boolean;
  skillsEnabled?: boolean;
  chargeRoleTerm: string;
}) {
  const { isSchedulingIncludeExcludeEnabled } = useFeatureFlags();
  const [deleteChange, { loading }] = useMutation(
    DELETE_WORKER_PROFILE_CHANGE_MUTATION,
    {
      refetchQueries: [
        'LoadBalancingWorker',
        'LoadBalancingWorkerSkills',
        'ScheduleGroupWorkers',
        'ScheduleReview',
      ],
      // NOTE: the client defaults errorPolicy to 'all' (legacy/lib/graph-ql.js), so GraphQL
      // errors resolve here with no worker instead of rejecting.
      onCompleted: (data) => {
        if (!data?.deleteWorkerProfileChange?.worker) {
          createMessage(
            'error',
            'Could not delete the change. Please try again.'
          );
          return;
        }
        createMessage('success', 'Scheduled change deleted.');
      },
      onError: () =>
        createMessage(
          'error',
          'Could not delete the change. Please try again.'
        ),
    }
  );

  const onDelete = (changeId: string) =>
    confirmDeleteChange(() =>
      deleteChange({
        variables: {
          workerId,
          scheduleGroupId,
          termId: changeId,
          schedulingPeriodId,
        },
      })
    );

  if (changes.length === 0) {
    return <Text secondary>No upcoming changes.</Text>;
  }

  return (
    <Gap
      vertical
      size="md"
      align="stretch"
      className="schedule-worker-upcoming-change"
    >
      {changes.map((change) => (
        <Box key={change.id} color="steelLightest">
          <Gap vertical size="xxs">
            <Flex justify="space-between" align="start" fullWidth>
              <Text weight="medium" block>
                Effective <LocalDate date={change.effectiveStartDate} />
                {change.effectiveEndDate && (
                  <>
                    – <LocalDate date={change.effectiveEndDate} />
                  </>
                )}
              </Text>
              {!readOnly && canManageProfile && (
                <Gap align="start" size="xs">
                  <LoadingState loading={loading} size="sm" />
                  <Dropdown
                    trigger={['click']}
                    menu={{
                      items: actionItems({
                        onEdit,
                        onDelete,
                        changeId: change.id,
                        disabled: loading,
                      }),
                    }}
                  >
                    <Button kind="link" size="sm" ariaLabel="More actions">
                      <Icon
                        icon="verticalEllipsis"
                        kind="regular"
                        size="lg"
                        fit
                      />
                    </Button>
                  </Dropdown>
                </Gap>
              )}
            </Flex>
            <AlignedInfo
              fullWidth
              content={changeRows({
                change,
                includeExcludeEnabled: isSchedulingIncludeExcludeEnabled,
                skillsEnabled,
                chargeRoleTerm,
              })}
            />
          </Gap>
        </Box>
      ))}
    </Gap>
  );
}

function changeRows({
  change,
  includeExcludeEnabled,
  skillsEnabled,
  chargeRoleTerm,
}: {
  change: TeamReviewChange;
  includeExcludeEnabled: boolean;
  skillsEnabled: boolean;
  chargeRoleTerm: string;
}): React.ReactNode[][] {
  const status = [
    includeExcludeEnabled ? 'Scheduling Status' : 'Status',
    schedulingStatusLabel(change.active, includeExcludeEnabled),
  ];
  const role = ['Role', change.role ?? '—'];
  const chargeEligible = [
    `${chargeRoleTerm} Eligible`,
    change.chargeEligible ? 'Yes' : '—',
  ];
  const skills = skillsEnabled ? [['Skills', skillsLabel(change.skills)]] : [];
  const preferences = [
    ['FTE', ftePercentageLabel(change.ftePercentage)],
    ['Shift Preference', shiftPreferenceLabel(change.preferredTime)],
    ['Shift Pattern', shiftPatternLabel(change.shiftPatternPreference)],
  ];

  return includeExcludeEnabled
    ? [status, role, chargeEligible, ...skills, ...preferences]
    : [role, ...preferences, chargeEligible, status, ...skills];
}

function actionItems({
  changeId,
  disabled,
  onEdit,
  onDelete,
}: {
  changeId: string;
  disabled: boolean;
  onEdit: (changeId: string) => void;
  onDelete: (changeId: string) => void;
}): NonNullable<MenuProps['items']> {
  return [
    {
      disabled,
      key: 'edit',
      label: 'Edit',
      onClick: () => onEdit(changeId),
    },
    {
      disabled,
      danger: true,
      key: 'delete',
      label: 'Delete',
      onClick: () => onDelete(changeId),
    },
  ];
}

function confirmDeleteChange(onConfirm: () => void) {
  Modal.confirm({
    title: 'Delete scheduled change?',
    content: `This scheduled change will be removed. The previous profile will extend forward to keep the timeline continuous. Existing shifts will not be automatically updated, and schedule metrics may change.`,
    okText: 'Delete',
    danger: true,
    onOk: onConfirm,
  });
}
